'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

export type UserRole = 'owner' | 'manager' | 'employee';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  phone: string;
  role: UserRole;
  is_active: boolean;
  company_id: string | null;
}

interface AuthContextValue {
  user: any;
  session: any;
  loading: boolean;
  userProfile: UserProfile | null;
  userRole: UserRole | null;
  companyId: string | null;
  isAdmin: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  resendVerificationEmail: (email: string) => Promise<void>;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);

  const supabase = createClient();
  const router = useRouter();

  const isAdmin = userRole === 'owner' || userRole === 'manager';

  const loadUserProfile = useCallback(async (userId: string) => {
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profile) {
        setUserProfile(profile as UserProfile);
        setUserRole(profile.role as UserRole);
        setCompanyId(profile.company_id ?? null);
      }
    } catch {
      // silently fail — profile may not exist yet
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user?.id) await loadUserProfile(user.id);
  }, [user, loadUserProfile]);

  useEffect(() => {
    // Restore session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setUserProfile(null);
        setUserRole(null);
        setCompanyId(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  // The database trigger (handle_new_user) now handles:
  //   - creating a new company/workspace for the user
  //   - setting role = 'owner'
  //   - linking user_profiles.company_id to the new company
  // We do NOT manually upsert user_profiles here to avoid overwriting
  // the trigger's company_id assignment.
  const signUp = async (
    email: string,
    password: string,
    metadata: { fullName?: string; companyName?: string; mobile?: string; country?: string } = {}
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata.fullName || '',
          phone: metadata.mobile || '',
          // Do NOT pass company_id here — absence signals "public signup"
          // and the trigger will create a new company automatically
        },
      },
    });
    if (error) throw error;

    // Profile + company creation is handled entirely by the DB trigger.
    // Just set the role optimistically so the UI doesn't flicker.
    if (data.user) {
      setUserRole('owner');
    }

    return data;
  };

  // Email/Password Sign In — supports Remember Me
  const signIn = async (email: string, password: string, rememberMe?: boolean) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;

    // Persist session in localStorage when Remember Me is checked
    if (rememberMe && data.session) {
      try {
        localStorage.setItem('sb_remember_me', 'true');
        localStorage.setItem('sb_access_token', data.session.access_token);
        localStorage.setItem('sb_refresh_token', data.session.refresh_token);
      } catch {
        // localStorage may be unavailable
      }
    } else {
      try {
        localStorage.removeItem('sb_remember_me');
        localStorage.removeItem('sb_access_token');
        localStorage.removeItem('sb_refresh_token');
      } catch {
        // ignore
      }
    }

    router.refresh();
    return data;
  };

  // Sign Out — clears all persisted session data
  const signOut = async () => {
    try {
      localStorage.removeItem('sb_remember_me');
      localStorage.removeItem('sb_access_token');
      localStorage.removeItem('sb_refresh_token');
    } catch {
      // ignore
    }
    // Clear service worker cache on logout to prevent data leakage between users
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUserProfile(null);
    setUserRole(null);
    setCompanyId(null);
    router.push('/sign-up-login-screen');
    router.refresh();
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at != null;
  };

  // Resend verification email
  const resendVerificationEmail = async (email: string) => {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: `${siteUrl}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  // Get User Profile from Database
  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data as UserProfile;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        userProfile,
        userRole,
        companyId,
        isAdmin,
        signUp,
        signIn,
        signOut,
        getCurrentUser,
        isEmailVerified,
        resendVerificationEmail,
        getUserProfile,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
